import React, { useState, useEffect, useContext, useRef } from 'react';
import '@/App.css';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { VideoContext } from '@/Pages/VideoPlayer/VideoContext';
import { useVideoData } from '@/v2/pages/Exercise/hooks/useVideoData';
import { useSavedVideos } from '@/v2/pages/Exercise/hooks/useSavedVideos';
import checkApiStatus from '@/component/SubscripStatus';
import { useFetchProducts } from '@/data/swrs/shopify';

// Components
import FilterSection from '@/v2/pages/Exercise/components/FilterSection';
import MobileSearchSection from '@/v2/pages/Exercise/components/MobileSearchSection';
import VideoGrid from '@/v2/pages/Exercise/components/VideoGrid';
import AddToPlayListModal from '@/component/playlist/personal-playlist/AddToPlayListModal';
import CustomPagination from '@/component/Pagination/CustomPagination';
import { useAuth } from '@/v2/hooks/useAuth';
import { useFilters } from '@/v2/hooks/useFilters';
import {getUniqueTagsFromVideos} from '@/utils/getUniqueTagsFromVideos';
import { getSignupRoute } from '@/constants/featureFlags';

export const Exercise = () => {
	const navigate = useNavigate();
	const userData = useAtomValue(userInfoAtom);
	const { selectedVideo, setSelectedVideo } = useContext(VideoContext);
	const { data: shopifyData } = useFetchProducts();
	const { isLoggedIn } = useAuth();

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 15;
	const [totalVideos, setTotalVideos] = useState(0);
	const [startIndex, setStartIndex] = useState(0);

	// Filter state
	const [selectedTags, setSelectedTags] = useState([]);
	const [selectedDurations, setSelectedDurations] = useState([]);
	const [selectedLevels, setSelectedLevels] = useState([]);
	const [selectedInstructors, setSelectedInstructors] = useState([]);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [sortBy, setSortBy] = useState('latest');
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [instructors, setInstructors] = useState([]);

	// Product state
	const [products, setProducts] = useState([{ name: '', id: '', subname: '' }]);

	// Playlist modal state
	const [modal, setModal] = useState(false);
	const [videoData, setVideoData] = useState({});
	const [selectedVideoData, setSelectedVideoData] = useState({});

	const { tags, handleResetFilter } = useFilters()

	const { responseData, fetchVideosByFilters, setResponseData } = useVideoData({
		startIndex,
		itemsPerPage,
		selectedTags,
		selectedDurations,
		selectedLevels,
		selectedInstructors,
		selectedProducts,
		sortBy,
		setTotalVideos,
		userData
	});

	const { savedVideoIds, handleSaveVideo } = useSavedVideos(userData, responseData);

	// Format products from Shopify data
	useEffect(() => {
		if (shopifyData?.products) {
			const formattedProducts = shopifyData.products.map(product => ({
				name: product?.title,
				id: product?.id,
				subname: product?.metafieldValue || ''
			}));
			setProducts(formattedProducts);
		}
	}, [shopifyData]);

	// Extract instructors from video data
	useEffect(() => {
		if (responseData?.length > 0 && instructors.length === 0) {
			setInstructors([
				...new Set(
					responseData[0]?.videos
						.filter(video => video.instructor !== undefined && video.instructor !== null)
						.map(video => video.instructor)
				)
			]);
		}
	}, [responseData, instructors.length]);

	// Load selected video data
	useEffect(() => {
		const data = window.localStorage.getItem('selectedVideo');
		if (data) {
			setVideoData(JSON.parse(data));
		}
	}, []);

	useEffect(() => {
		if (selectedVideo) {
			setVideoData(selectedVideo);
		}
	}, [selectedVideo]);

	// Fetch videos when filters change
	useEffect(() => {
		fetchVideosByFilters();
	}, [currentPage, selectedTags, selectedDurations, selectedLevels, sortBy, selectedInstructors, selectedProducts]);

	// Pagination handler
	const handlePageChange = newPage => {
		window.scrollTo(0, 0);
		if (newPage <= 0) {
			newPage = 1;
		}
		setCurrentPage(newPage);
		const newStartIndex = (newPage - 1) * itemsPerPage;
		setStartIndex(newStartIndex);
	};

	// Open video player
	const handlePlayerVideo = async id => {
		if (isLoggedIn) {
			const apiStatus = await checkApiStatus(userData.token);
			if (apiStatus) {
				navigate(`/PlayerPage?v=${id}`);
			} else {
				navigate('/Plans');
			}
	} else {
			navigate(getSignupRoute());
		}
	};

	// Handle Add to Playlist
	const handleAddToPlaylist = async videoId => {
		try {
			const response = await api.videoPlayerPage(videoId, userData.token);
			if (response.status === 200) {
				setSelectedVideoData(response.data[0]);
				setModal(true);
			}
		} catch (error) {
			toast.error('Something went wrong', {
				position: 'top-center',
				duration: 3000
			});
		}
	};

	// Handle shuffle videos
	const handleShuffle = categoryId => {
		setResponseData(prevData => {
			// Find the category to shuffle
			const shuffledCategory = prevData.find(category => category._id === categoryId);

			// Shuffle the videos in the category
			if (shuffledCategory) {
				shuffledCategory.videos = shuffleArray(shuffledCategory.videos);
			}

			return [...prevData];
		});
	};

	const shuffleArray = array => {
		const shuffledArray = [...array];
		for (let i = shuffledArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
		}
		return shuffledArray;
	};

	// Filter modal handlers
	const handleOpenFilterModal = () => setIsFilterModalOpen(true);
	const handleCloseFilterModal = () => setIsFilterModalOpen(false);

	const handleApplyFilter = (tags, durations, levels, instructors, products) => {
		setSelectedTags(tags);
		setSelectedLevels(levels);
		setSelectedDurations(durations);
		setSelectedInstructors(instructors);
		setSelectedProducts(products);
		setIsFilterModalOpen(false);
	};


	const handleFilterChange = event => {
		setSortBy(event.target.value);
	};
	const initialFocusRef = useRef([]);
	
	const uniqueTags = getUniqueTagsFromVideos(responseData[0]?.videos, initialFocusRef);
		
	return (
		<>
			{/* Mobile View */}
			<MobileSearchSection
				handleOpenFilterModal={handleOpenFilterModal}
				isFilterModalOpen={isFilterModalOpen}
				handleFilterChange={handleFilterChange}
				sortBy={sortBy}
				tags={tags}
				selectedTags={selectedTags}
				selectedDurations={selectedDurations}
				selectedLevels={selectedLevels}
				selectedInstructors={selectedInstructors}
				selectedProducts={selectedProducts}
				handleApplyFilter={handleApplyFilter}
				handleResetFilter={handleResetFilter}
				handleCloseFilterModal={handleCloseFilterModal}
				instructors={instructors}
				products={products}
			/>

			{/* Desktop View */}
			<div>
				<div className="tab-content fade-in" style={{ minHeight: '100vh' }}>
					<div id="menu2" className="tab-pane fade active show">
						{responseData?.map((category, index) => (
							<div key={index} id={category?.name?.split(' ')[0]}>
								{/* Filter Section */}
								<FilterSection
									category={category}
									handleOpenFilterModal={handleOpenFilterModal}
									isFilterModalOpen={isFilterModalOpen}
									tags={tags}
									selectedTags={selectedTags}
									selectedDurations={selectedDurations}
									selectedLevels={selectedLevels}
									selectedInstructors={selectedInstructors}
									selectedProducts={selectedProducts}
									handleApplyFilter={handleApplyFilter}
									handleResetFilter={handleResetFilter}
									handleCloseFilterModal={handleCloseFilterModal}
									handleFilterChange={handleFilterChange}
									sortBy={sortBy}
									instructors={instructors}
									products={products}
								/>
								{/* {category?.videos?.length  === 0 && (
									<NoVideosFoundSection/>
								)} */}
								{/* Video Grid */}
								<VideoGrid
									category={category}
									handleShuffle={handleShuffle}
									isLoggedIn={isLoggedIn}
									handlePlayerVideo={handlePlayerVideo}
									handleAddToPlaylist={handleAddToPlaylist}
									handleSaveVideo={handleSaveVideo}
									savedVideoIds={savedVideoIds}
									selectedTags={selectedTags}
									setSelectedTags={setSelectedTags}
									modal={modal}
									setModal={setModal}
									setVideoData={setSelectedVideoData}
									selectTagCallback={tag => {
										setSelectedTags(tags => {
											if (tags.includes(tag._id)) {
												return tags.filter(t => t !== tag._id);
											}
											return [...tags, tag._id];
										});
									}}
								/>

								{/* Pagination */}
								{category?.videos?.length > 0 && (
									<CustomPagination totalPages={totalVideos} currentPage={currentPage} onPageChange={handlePageChange} />
								)}

							</div>
						))}
					</div>
				</div>
			</div>

			{/* Add to Playlist Modal */}
			<AddToPlayListModal modal={modal} setModal={setModal} activeObject={selectedVideoData} viewData={''} />
		</>
	);
};
